import type { ReactElement } from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../lib/cn';

type CommentBankCardProps = {
  templates: string[];
  onInsert: (text: string) => void;
  className?: string;
};

export function CommentBankCard({
  templates,
  onInsert,
  className,
}: CommentBankCardProps): ReactElement | null {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return templates;
    return templates.filter((tpl) => tpl.toLowerCase().includes(needle));
  }, [templates, query]);

  if (templates.length === 0) return null;

  return (
    <div
      className={cn(
        'rounded-dg-card border border-slate-200/80 bg-white p-4 shadow-sm dark:border-slate-700/70 dark:bg-slate-900/50',
        className
      )}
    >
      <h3 className="text-dg-card-title font-semibold text-slate-900 dark:text-white">
        {t('audit.gradeStudio.quickComments')}
      </h3>
      <label htmlFor="dg-comment-bank-search" className="sr-only">
        {t('audit.gradeStudio.commentSearchLabel')}
      </label>
      <input
        id="dg-comment-bank-search"
        type="search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={t('audit.gradeStudio.commentSearchPlaceholder')}
        className="mt-3 h-10 w-full rounded-dg-control border border-slate-200/90 bg-white px-3 text-dg-body outline-none focus:border-dg-accent-grade/40 dark:border-slate-600 dark:bg-slate-950 dark:text-white"
        autoComplete="off"
      />
      <div className="mt-3 flex flex-wrap gap-2">
        {filtered.length === 0 ? (
          <p className="text-dg-secondary text-slate-500 dark:text-slate-400">
            {t('audit.gradeStudio.commentSearchEmpty')}
          </p>
        ) : (
          filtered.map((template) => (
            <button
              key={template}
              type="button"
              onClick={() => onInsert(template)}
              className="rounded-dg-control border border-dg-accent-grade/25 bg-dg-accent-gradeLight/70 px-3 py-2 text-left text-dg-body font-medium text-amber-950 transition-colors hover:border-dg-accent-grade/50 hover:bg-dg-accent-gradeLight dark:border-amber-800/40 dark:bg-amber-950/25 dark:text-amber-100 dark:hover:bg-amber-950/40"
            >
              {template}
            </button>
          ))
        )}
      </div>
    </div>
  );
}
